import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Output, ViewChild, computed, effect, inject, input, signal } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Design } from 'src/app/interfaces/design';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { DesignCategory } from 'src/app/interfaces/design-category';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-design-search',
  templateUrl: './design-search.component.html',
  styleUrls: ['./design-search.component.scss']
})
export class DesignSearchComponent {
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private designsService = inject(DesignsService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  @Output() closeClick = new EventEmitter();
  @Output() filteredDesignsChange = new EventEmitter<Design[]>();
  @Output() filteredCategoriesChange = new EventEmitter<DesignCategory[]>();
  @ViewChild('search', { static: true }) searchInput!: ElementRef<HTMLInputElement>;
  // designs: Design[] = DESIGNS.filter(d => VALID_ITEMS.some(v => v.design === d.shortName));
  designs = computed(() => this.designsService.values());
  categories = computed(() => this.designsService.categories());
  designsToFilter: Design[] = [];
  uniqueDesignCategories = signal<DesignCategory[]>([]);
  selectedDesignCategories = signal<DesignCategory[]>([]);
  categoriesToFilterBy = computed(() => this.selectedDesignCategories().length > 0 ? this.selectedDesignCategories() : this.uniqueDesignCategories());
  shownDesigns: Design[] = [];
  searchText: string | null = null;
  selectedDesign = computed(() => this.selectedAttributeService.getValue().design);
  shouldShowUnavailable = computed(() => this.selectedAttributeService.shouldShowUnavailable());
  // availableDesigns = computed(() => );
  availableDesigns = input<number[]>([]);
  private lastAvailableDesignCount = 0;

  constructor() {
    effect(() => {
      const availableDesigns = this.availableDesigns();
      const isShowingUnavailable = this.shouldShowUnavailable();

      if (isShowingUnavailable) {
        this.designsToFilter = this.designs();
      } else {
        const selectedDesignCode = this.selectedDesign()?.shortName;
        this.designsToFilter = this.designs().filter(d => availableDesigns.includes(d.id) || d.shortName === selectedDesignCode);
      }

      this.uniqueDesignCategories.set(this.categories().filter(c => this.designsToFilter.some(d => d.categories.includes(c.id))));

      if (this.lastAvailableDesignCount != availableDesigns.length) {
        this.lastAvailableDesignCount = availableDesigns.length;
      }

      this.changeDetectorRef.detectChanges();
    }, { allowSignalWrites: true });
  }

  searchTextChange() {
    this.shownDesigns = this.getFilteredDesignsWithText(this.searchText);
    this.filteredDesignsChange.emit(this.shownDesigns);
  }

  async designClick(design: Design) {
    if (this.selectedDesign()?.displayName === design.displayName) {
      await this.selectedAttributeService.mutateDesign(null);
    } else {
      await this.selectedAttributeService.mutateDesign(design);
    }
  }

  showUnavailableChange(value: boolean) {
    this.selectedAttributeService.shouldShowUnavailable.set(value);
  }

  categoryFilterChange(newSelectedDesignCategories: DesignCategory[]) {
    this.selectedDesignCategories.set(newSelectedDesignCategories);
    this.shownDesigns = this.getFilteredDesignsWithText(this.searchText);
    this.filteredDesignsChange.emit(this.shownDesigns);
    this.filteredCategoriesChange.emit(this.selectedDesignCategories());

    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('filter', 'attribute_filter', 'design_category_filter', newSelectedDesignCategories.map(c => c.name).join(', '));
    }
  }

  getFilteredDesignsWithText(searchText: string | null) {
    if (searchText) {
      const startsWith = this.designsToFilter.filter(d => d.displayName.toLowerCase().startsWith(searchText.toLowerCase()) || d.tags.some(tag => tag.toLowerCase().startsWith(searchText.toLowerCase())));
      const contains = this.designsToFilter.filter(d => !startsWith.concat(startsWith).some(s => s.displayName === d.displayName) && (d.displayName.toLowerCase().includes(searchText!.toLowerCase()) || d.tags.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))));
      return Array.from(new Set(startsWith.concat(contains).slice(0,200))).filter(d => this.categoriesToFilterBy().some(dc => d.categories.includes(dc.id)));
    } else {
      return this.designsToFilter;
    }
  }

  compareCategories(a: DesignCategory, b: DesignCategory) {
    return a.id === b.id;
  }

  onInputBlur() {
    if (this.googleAnalyticsService.isInitialized && this.searchText) {
      this.googleAnalyticsService.triggerEvent('search', 'attribute_filter', 'search_filter', this.searchText);
    }
  }
}
